import React, { useEffect, useState } from 'react'
import PropTypes from 'propTypes'
import { useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import { cx } from '@emotion/css'
import {
  Container,
  Checkbox,
  FormLabel,
  Heading,
  Hyperlink,
  LazyImage,
  requiredField,
  TextInput,
  SingleOption,
  SingleSelect,
  CountryCodePhoneInput,
  SubmitButton,
  phoneMinLength,
  maxLengthText,
  countryPhonePrefixRequired,
  minLength,
  InputWithRedux,
} from '@offerzen/pinata-shame'
import ControlledHookField from '@/core/components/ControlledHookField'
import { createMemberProfile } from '@/core/apiModules/memberProfile'

import styles from './styles'

const PhoneInput = InputWithRedux(CountryCodePhoneInput)

const roles = [
  'Co-Founder/CEO/Management',
  'Hiring Manager',
  'Investor',
  'HR/Internal Recruiter',
  'Contract Recruiter',
  'Developer',
  'Other',
]

const validators = {
  required: requiredField('Required'),
  requiredRole: requiredField('You must select a role'),
  requiredPassword: requiredField(''),
  password: minLength(14, ''),
  requiredPhone: requiredField('You must add a phone number'),
  phoneMinLength: phoneMinLength(),
  maxLengthText: maxLengthText(128),
  countryPhonePrefixRequired: countryPhonePrefixRequired(),
}

const NewMemberRegistrationForm = ({ companyName, email, name, token, refetchCurrentUser }) => {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [roleSelection, setRoleSelection] = useState('')

  useEffect(() => {
    const newBackgroundColor = '#132254'

    // Get the original background colors for both the html and body tags
    const originalHtmlBackgroundColor = document.documentElement.style.backgroundColor
    const originalBodyBackgroundColor = document.body.style.backgroundColor

    // Update them with what's required for invitation acceptance
    document.documentElement.style.backgroundColor = newBackgroundColor
    document.body.style.backgroundColor = newBackgroundColor

    // When the component is removed from the DOM, change the background colors back as not to affect any other pages
    return () => {
      document.documentElement.style.backgroundColor = originalHtmlBackgroundColor
      document.body.style.backgroundColor = originalBodyBackgroundColor
    }
  }, [])

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors: formErrors },
  } = useForm({ mode: 'onTouched', defaultValues: { opt_into_marketing: false, email, name } })

  // default values arent being populated as it's only on first render. Use reset to repopulate
  useEffect(() => {
    reset({ name, email })
  }, [name, email, reset])

  const { mutate: createNewMemberProfile, isLoading } = useMutation(
    ['createMemberProfile'],
    data => {
      const { otherRole, ...user } = data.user
      const userAttributes = {
        ...user,
        role: data.user.otherRole || data.user.role,
      }

      return createMemberProfile({ user: userAttributes })
    },
    {
      onSuccess: data => {
        refetchCurrentUser()
        window.location.href = data.url
      },
      onError: error => {
        const errorData = error.response.data

        setError(errorData.field, { type: 'error', message: errorData.error_message })
      },
    },
  )

  const handleFormSubmit = params => {
    const formParams = { user: { ...params, email, token } }
    createNewMemberProfile(formParams)
  }

  return (
    <Container className={styles.container}>
      <div className={styles.imageContainer}>
        <LazyImage className={styles.logoImage} loader={() => import('../../../assets/offerzen-logo.svg')} />
      </div>

      <div className={styles.formContainer}>
        <Heading className={styles.heading}>{(companyName && `Join the ${companyName} team!`) || ''}</Heading>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className={styles.inputContainer}>
            <FormLabel className={styles.label} label="Your full name" htmlFor="name" />
            <ControlledHookField
              name="name"
              id="name"
              className={styles.input}
              component={TextInput}
              control={control}
              validate={[validators.required]}
            />
          </div>

          <div className={styles.inputContainer}>
            <FormLabel className={styles.label} label="Your email address" htmlFor="email" />
            <ControlledHookField name="email" id="email" className={styles.input} component={TextInput} control={control} disabled />
          </div>

          <div className={cx(styles.inputContainer, styles.passwordContainer)}>
            <FormLabel className={styles.label} label="Your password" htmlFor="password" />
            <div className={styles.passwordToggleContainer}>
              <ControlledHookField
                name="password"
                id="password"
                type={(passwordVisible && 'text') || 'password'}
                className={cx(styles.input, styles.password)}
                component={TextInput}
                control={control}
                validate={[validators.requiredPassword]}
              />
              <span className={styles.imageToggleContainer} onClick={() => setPasswordVisible(!passwordVisible)}>
                <If condition={!passwordVisible}>
                  <LazyImage className={styles.image} loader={() => import('../../../assets/eye-off.svg')} alt="" width={20} />
                </If>
                <If condition={passwordVisible}>
                  <LazyImage className={styles.image} loader={() => import('../../../assets/eye.svg')} alt="" width={20} />
                </If>
              </span>
            </div>
            <small className={cx(styles.passwordSubtext, formErrors.password && styles.passwordError)}>
              Your password must contain at least 14 characters
            </small>
          </div>

          <div className={styles.selectContainer}>
            <FormLabel className={styles.label} label="What best describes your current role?" htmlFor="role" />
            <ControlledHookField
              name="role"
              id="role"
              component={SingleSelect}
              control={control}
              validate={[validators.requiredRole]}
              onChange={e => {
                setRoleSelection(e)
              }}
              placeholder="-- Choose One --">
              <For each="option" of={roles}>
                <SingleOption key={option} value={option} label={option} />
              </For>
            </ControlledHookField>
          </div>

          <div className={styles.inputContainer}>
            {roleSelection === 'Other' && (
              <>
                <FormLabel className={styles.label} label="Please specify 'other':" htmlFor="otherRole" />
                <ControlledHookField
                  name="otherRole"
                  id="otherRole"
                  className={styles.input}
                  component={TextInput}
                  control={control}
                  validate={[validators.required]}
                />
              </>
            )}
          </div>

          <div className={styles.inputContainer}>
            <FormLabel className={styles.label} label="Your telephone number" htmlFor="phone" />
            <ControlledHookField
              name="phone"
              id="phone"
              className={styles.countryCodeInput}
              component={PhoneInput}
              control={control}
              hideLabel
              validate={[
                validators.requiredPhone,
                validators.phoneMinLength,
                validators.maxLengthText,
                validators.countryPhonePrefixRequired,
              ]}
            />
          </div>

          <div className={styles.inputContainer}>
            <ControlledHookField
              name="opt_into_marketing"
              id="opt_into_marketing"
              className={styles.checkboxContainer}
              // eslint-disable-next-line max-len
              label="Let me know about job-seeking developers and other marketplace insights, like data reports, events, and salary content. Unsubscribe at any time."
              component={Checkbox}
              control={control}
            />

            <ControlledHookField
              name="accepted_terms_of_service"
              id="accepted_terms_of_service"
              className={styles.checkboxContainer}
              label="I agree to the Privacy Policy, Terms of Use and use of cookies as described in our Cookie Declaration."
              component={Checkbox}
              control={control}
              validate={[validators.required]}
            />
          </div>

          <small className={styles.terms}>
            Read our <Hyperlink external label="Privacy Policy" href="/privacy" target="_blank" rel="noopener noreferrer" />,{' '}
            <Hyperlink external label="Terms of Use" href="/terms/employer" target="_blank" rel="noopener noreferrer" /> and{' '}
            <Hyperlink external label="Cookie Declaration" href="/cookie-declaration" target="_blank" rel="noopener noreferrer" />
          </small>

          <div className={styles.submitContainer}>
            <SubmitButton submitLabel="Join my team" submittingLabel="Loading" isSubmitting={isLoading} className={styles.submit} />
          </div>

          <div className={styles.login}>
            Already have an account? <Hyperlink label="Log in" external href="/users/sign_in" />
          </div>
        </form>
      </div>
    </Container>
  )
}
const { string, func } = PropTypes

NewMemberRegistrationForm.propTypes = {
  companyName: string,
  email: string,
  name: string,
  token: string,
  refetchCurrentUser: func,
}

export default NewMemberRegistrationForm
